import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Layout, Button, Row, Col, message, Modal, Form, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  getBuyersService,
  getBuyerService,
  getSaleService,
  getMaterialService,
  getMetalsService,
  postBuyerSelectedService,
  postAddToSaleService,
  postBuyerService,
  postMetalService,
  putCompleteSaleService,
  putMaterialService,
  deleteMaterialService,
  getCategoriesService,
  putSaleDateService,
} from "./NewSalePageService";
import Header from "../../components/Header/Header";
import Sidebar from "../../components/Sidebar/Sidebar";
import ScaleReading from "../../components/ScaleReading/ScaleReading";
import SaleForm from "../../components/SaleForm/SaleForm";
import Loader from "../../components/Loader/Loader";
import "./NewSalePage.css";
import { removeTokenFromCookies } from "../../utils/cookies";
import Cookies from "universal-cookie";
import Copyright from "../../components/Copyright/Copyright";
import ScaleCamera from "../../components/ScaleCamera/ScaleCamera";
import { webcamDeviceIDs } from "../../constants/webcamIDs";

const { Content } = Layout;

export interface NewSalePageProps {
  scaleSerialPort: any;
  scaleReader: any;
  updateScaleSerialPort: (port: any) => void;
  updateScaleReader: (reader: any) => void;
}

const NewSalePage = (props: NewSalePageProps) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [buyers, setBuyers] = useState([]);
  const [weight, setWeight] = useState("0.0");
  const [saleID, setSaleID] = useState("");
  const [portAccessModalOpen, setPortAccessModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [weightReadingTextDummy, setWeightReadingTextDummy] = useState("");
  const [scaleImageSrc, setScaleImageSrc] = useState("");
  const [materialImageSrc, setMaterialImageSrc] = useState("");
  const [showSelectBuyerForm, setShowSelectBuyerForm] = useState(true);
  const [selectedBuyerInfo, setSelectedBuyerInfo] = useState({}) as any;
  const [saleInfo, setSaleInfo] = useState({}) as any;
  let location = useLocation();
  let navigate = useNavigate();
  const scaleWebcamRef = useRef(null) as any;
  const materialWebcamRef = useRef(null) as any;
  const cookies = new Cookies();
  const token = cookies.get("TOKEN");
  const currOffice = cookies.get("CURR_OFFICE");

  useEffect(() => {
    navToSelectOffice();
    isPortEnabled();
    if (token) {
      getBuyers();
    }
  }, [token, props.scaleSerialPort]);

  useEffect(() => {
    if (props.scaleReader !== null) getReadingValue(props.scaleReader);
  }, [props.scaleReader, weightReadingTextDummy]);

  const navToSelectOffice = () => {
    if (currOffice === "" || currOffice === undefined || currOffice === null) navigate("/");
  };

  const navToLogin = () => {
    navigate("/login");
  };

  const isPortEnabled = () => {
    if (props.scaleSerialPort === null) setPortAccessModalOpen(true);
  };

  const getSale = async (saleIDFromURL: string) => {
    try {
      setLoading(true);
      const res = await getSaleService(currOffice, saleIDFromURL);
      setSaleID(res.data.saleID);
      setLoading(false);
      return res.data;
    } catch (e: any) {
      setLoading(false);
      console.error("getSale error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const getMaterial = async (materialID: string) => {
    try {
      setLoading(true);
      const res = await getMaterialService(materialID);
      setLoading(false);
      return res.data;
    } catch (e: any) {
      setLoading(false);
      console.error("getMaterial error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const toggleSidebar = (collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
  };

  const getBuyers = async () => {
    try {
      setLoading(true);
      const res = await getBuyersService();
      const buyersData = currOffice === "pg" ? res.data.pgBuyers : currOffice === "bw" ? res.data.bwBuyers : [];
      setBuyers(buyersData);
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      console.error("getBuyers error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const getMetals = async () => {
    try {
      setLoading(true);
      const res = await getMetalsService();
      // setMetals(res.data);
      setLoading(false);
      return res.data;
    } catch (e: any) {
      setLoading(false);
      console.error("getMetals error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const getCategories = async () => {
    try {
      setLoading(true);
      const res = await getCategoriesService();
      setLoading(false);
      return res.data;
    } catch (e: any) {
      setLoading(false);
      console.error("getCategories error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const postBuyerSelected = async (buyerID: string, dateString: string) => {
    try {
      const dateProperties = dateString.split("/");
      const date =
        dateString !== "" && dateString !== undefined
          ? new Date(`${dateProperties[2]}-${dateProperties[1]}-${dateProperties[0]}`).toISOString()
          : new Date().toISOString();
      setLoading(true);
      const payload = {
        buyerID: buyerID,
        office: currOffice,
        date: date,
      };
      const res = await postBuyerSelectedService(payload);
      setSaleInfo(res.data);
      getSelectedBuyerInfo(res.data.buyerID);
      if (res.data.saleID) {
        setSaleID(res.data.saleID);
        window.history.pushState(null, "", `sale/${res.data.saleID}`);
      }
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      console.error("postBuyerSelected error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const getSelectedBuyerInfo = (buyerID: any) => {
    if (buyers === undefined || buyers.length === 0) return;
    const buyerInfo = buyers.find((buyer: any) => buyer.buyerID === buyerID);
    setSelectedBuyerInfo(buyerInfo);
    setShowSelectBuyerForm(false);
  };

  const putMaterial = async (materialInfo: any) => {
    try {
      setLoading(true);
      const payload = {
        materialID: materialInfo.materialID,
        name: materialInfo.name,
        rawWeight: materialInfo.rawWeight,
        wasteWeight: materialInfo.wasteWeight,
        netWeight: String(materialInfo.netWeight),
        manualEntry: materialInfo.manualEntry,
        smallBag: materialInfo.smallBag,
        bigBag: materialInfo.bigBag,
        jumboBag: materialInfo.jumboBag,
        other: materialInfo.other,
        metalID: materialInfo.metalID,
        scaleImageSrc: scaleImageSrc,
        materialImageSrc: materialImageSrc,
        notes: materialInfo.notes,
        office: currOffice,
      };
      const res = await putMaterialService(payload);
      setScaleImageSrc("");
      setMaterialImageSrc("");
      setLoading(false);
      return res;
    } catch (e: any) {
      setLoading(false);
      console.error("putSale error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const postAddToSale = async (materialInfo: any) => {
    try {
      setLoading(true);
      const payload = {
        materialID: materialInfo.key,
        name: materialInfo.name,
        rawWeight: materialInfo.rawWeight,
        wasteWeight: materialInfo.wasteWeight,
        netWeight: materialInfo.netWeight,
        price: 0.0,
        saleID: saleID,
        notes: materialInfo.notes,
        manualEntry: materialInfo.manualEntry,
        smallBag: materialInfo.smallBag,
        bigBag: materialInfo.bigBag,
        jumboBag: materialInfo.jumboBag,
        other: materialInfo.other,
        office: currOffice,
        metalID: materialInfo.metalID,
        scaleImageSrc: materialInfo.scaleImageSrc,
        materialImageSrc: materialInfo.materialImageSrc,
        packageType: materialInfo.packageType,
      };
      const res = await postAddToSaleService(payload);
      setScaleImageSrc("");
      setMaterialImageSrc("");
      setLoading(false);
      return res.data;
      // if (res.data.saleID) setSaleID(res.data.saleID);
    } catch (e: any) {
      setLoading(false);
      console.error("postAddToSale error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const postBuyer = async (buyerInfo: any) => {
    try {
      setLoading(true);
      const payload = {
        name: buyerInfo.name,
        companyName: buyerInfo.companyName,
        identificationCardNumber: buyerInfo.identificationCardNumber.replaceAll(" ", "").replaceAll("-", ""),
        contactNumber: "",
        email: "",
        address: "",
        companyCode: "",
        outstandingAmount: 0.0,
        passportNumber: buyerInfo.passportNumber,
      };
      const res = await postBuyerService(payload);
      setTimeout(async () => {
        setLoading(false);
        await getBuyers();
      }, 1000);
    } catch (e: any) {
      setLoading(false);
      console.error("postBuyer error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const postMetal = async (metalInfo: any) => {
    try {
      setLoading(true);
      const payload = {
        name: metalInfo.name.trim(),
        displayName:
          metalInfo.displayName === "" || metalInfo.displayName === null || metalInfo.displayName === undefined
            ? metalInfo.name.trim()
            : metalInfo.displayName.trim(),
        category: metalInfo.category,
      };
      const res = await postMetalService(payload);
      setTimeout(async () => {
        setLoading(false);
        await getMetals();
      }, 1000);
    } catch (e: any) {
      setLoading(false);
      console.error("postMetal error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const postCompleteSale = async (
    materialsList: any[],
    notes: string,
    totalNetWeight: Number,
    totalNumMaterials: Number
  ) => {
    try {
      setLoading(true);
      const payload: any = {
        saleID: saleID,
        completed: true,
        buyerInfo: selectedBuyerInfo,
        notes: notes,
        totalNetWeight: totalNetWeight,
        totalNumMaterials: totalNumMaterials,
      };
      const resSale = await getSale(saleID);
      payload.saleDate = resSale.createdTimestamp;
      const res = await putCompleteSaleService(payload);
      setLoading(false);
      navigate("/sales");
      // if (res.data.saleID) setSaleID(res.data.saleID);
    } catch (e: any) {
      setLoading(false);
      console.error("postCompleteSale error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const putSaleDate = async (dateString: string) => {
    try {
      setLoading(true);
      const dateProperties = dateString.split("/");
      const date =
        dateString !== "" && dateString !== undefined
          ? new Date(`${dateProperties[2]}-${dateProperties[1]}-${dateProperties[0]}`).toISOString()
          : new Date().toISOString();
      const payload: any = {
        date: date,
      };
      const resUpdateDate = await putSaleDateService(saleID, payload);
      // let currSaleInfo = { ...saleInfo };
      // currSaleInfo.createdTimestamp = new Date(date).toLocaleDateString("en-GB");
      // setSaleInfo(currSaleInfo);
      setLoading(false);
      window.location.reload();
    } catch (e: any) {
      setLoading(false);
      console.error("putSaleDate error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const recordMaterial = () => {
    setScaleImageSrc("");
    setMaterialImageSrc("");
    setTimeout(() => {
      const scaleImage = captureScaleImage();
      const materialImage = captureMaterialImage();
      // return {
      //   rawWeight: Number(weight).toFixed(1),
      //   scaleImage: scaleImage,
      //   materialImage: materialImage,
      // };
    }, 500);
    return Number(weight).toFixed(1);
  };

  const captureScaleImage = useCallback(() => {
    if (scaleImageSrc === "") {
      if (scaleWebcamRef.current !== "") {
        const scaleImageSrc = scaleWebcamRef.current.getScreenshot();
        setScaleImageSrc(scaleImageSrc);
        return scaleImageSrc;
      } else {
        setScaleImageSrc("");
        setTimeout(() => {
          const scaleImageSrc = scaleWebcamRef.current.getScreenshot();
          setScaleImageSrc(scaleImageSrc);
          return scaleImageSrc;
        }, 1000);
      }
    }
  }, [scaleWebcamRef]);

  const captureMaterialImage = useCallback(() => {
    if (materialImageSrc === "") {
      if (materialWebcamRef.current !== "") {
        const materialImageSrc = materialWebcamRef.current.getScreenshot();
        setMaterialImageSrc(materialImageSrc);
        return materialImageSrc;
      } else {
        setMaterialImageSrc("");
        setTimeout(() => {
          const materialImageSrc = materialWebcamRef.current.getScreenshot();
          setMaterialImageSrc(materialImageSrc);
          return materialImageSrc;
        }, 1000);
      }
    }
  }, [materialWebcamRef]);

  const resetImageViews = () => {
    setScaleImageSrc("");
    setMaterialImageSrc("");
  };

  const getReadingValue = async (reader: any) => {
    let weightReadingText = "";
    const { value, done } = await reader.read();
    setWeightReadingTextDummy(String(Math.random()));
    if (value) {
      weightReadingText += value;
      if (/\r\n/i.test(weightReadingText)) {
        let recordWeight = weightReadingText.trim().split("kg")[0];
        if (!isNaN(Number(recordWeight))) setWeight(Number(recordWeight).toFixed(1));
        weightReadingText = "";
      }
    }
    if (done) {
      reader.releaseLock();
    }
  };

  const getPortAccess = async () => {
    try {
      setPortAccessModalOpen(false);
      const port = await (window.navigator as any).serial.requestPort();
      props.updateScaleSerialPort(port);
      await port.open({ baudRate: 9600, bufferSize: 1000000 });
      const textDecoder = new TextDecoderStream();
      port.readable.pipeTo(textDecoder.writable);
      const reader = textDecoder.readable.getReader();
      props.updateScaleReader(reader);
    } catch (e: any) {
      setPortAccessModalOpen(false);
      console.error("getPortAccess error", e);
    }
  };

  const updateLoading = (loading: boolean) => {
    setLoading(loading);
  };

  const resetNewSalePage = () => {
    toggleSidebar(true);
  };

  const deleteMaterial = async (materialID: string) => {
    try {
      setLoading(true);
      const payload: any = {
        materialID: materialID,
        saleID: saleID,
        office: currOffice,
      };
      const res = await deleteMaterialService(payload);
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      console.error("deleteMaterial error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  return (
    <Layout>
      <Loader loading={loading} />
      <Modal
        title="Allow COM port for Scale Reading (RS323)"
        open={portAccessModalOpen}
        okText="Allow"
        cancelButtonProps={{ style: { display: "none" } }}
        onOk={getPortAccess}
        onCancel={() => setPortAccessModalOpen(false)}
      >
        <p>Select 'USB-Serial Controller (COM3)' and press Connect</p>
      </Modal>
      <Sidebar collapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} />
      <Content>
        <Col span={24}>
          <Header toggleSidebar={toggleSidebar} />
          <Row className="new-sale-container">
            <Col style={{ width: "25%" }}>
              <Row className="new-sale-weight-reading">
                <ScaleReading weight={weight} scaleSerialPort={props.scaleSerialPort} getPortAccess={getPortAccess} />
              </Row>
              <Row className="new-sale-image-capture">
                <ScaleCamera
                  webcamRef={scaleWebcamRef}
                  webcamName="Scale"
                  imageSrc={scaleImageSrc}
                  // deviceID={webcamDeviceIDs.scaleCameraPG}
                  deviceID={currOffice === "pg" ? webcamDeviceIDs.scaleCameraPG : webcamDeviceIDs.scaleCameraBW}
                />
              </Row>
              <Row className="new-sale-image-capture">
                <ScaleCamera
                  webcamRef={materialWebcamRef}
                  webcamName="Material"
                  imageSrc={materialImageSrc}
                  // deviceID={webcamDeviceIDs.materialCameraPG}
                  deviceID={currOffice === "pg" ? webcamDeviceIDs.scaleCameraPG : webcamDeviceIDs.scaleCameraBW}
                />
              </Row>
            </Col>
            <Col style={{ width: "75%" }} className="new-sale-form">
              <SaleForm
                scaleSerialPort={props.scaleSerialPort}
                captureScaleImage={captureScaleImage}
                captureMaterialImage={captureMaterialImage}
                buyers={buyers}
                selectedBuyerInfo={selectedBuyerInfo}
                postBuyerSelected={postBuyerSelected}
                recordMaterial={recordMaterial}
                postAddToSale={postAddToSale}
                putMaterial={putMaterial}
                postCompleteSale={postCompleteSale}
                getSale={getSale}
                getMaterial={getMaterial}
                updateLoading={updateLoading}
                resetNewSalePage={resetNewSalePage}
                postBuyer={postBuyer}
                postMetal={postMetal}
                deleteMaterial={deleteMaterial}
                getMetals={getMetals}
                getCategories={getCategories}
                resetImageView={resetImageViews}
                scaleImageSrc={scaleImageSrc}
                materialImageSrc={materialImageSrc}
                showSelectBuyerForm={showSelectBuyerForm}
                saleInfo={saleInfo}
                getSelectedBuyerInfo={getSelectedBuyerInfo}
                updateSaleDate={putSaleDate}
              />
            </Col>
          </Row>
        </Col>
        <Copyright />
      </Content>
    </Layout>
  );
};

export default NewSalePage;
